import React, { useEffect, useRef, useState } from "react";
import { posAPI } from "../../services/api";
import { useI18n } from "../../i18n";
import { formatMoney } from "../utils/money";

const SEARCH_DELAY_MS = 200;

const sameName = (a, b) =>
  a.replace(/\s+/g, " ").trim().toLowerCase() ===
  b.replace(/\s+/g, " ").trim().toLowerCase();

/**
 * Whose tab a credit sale goes on, when no customer card is attached.
 *
 * Typing finds existing tabs — by name, or the end of a phone number — so a
 * regular's debts stay on one tab instead of scattering over "Ahmed",
 * "ahmed" and "Ahmed B". A name nobody has yet opens a new tab. Offline the
 * list cannot load, but a typed name still works: the server finds or opens
 * the tab when the sale reaches it.
 *
 * value: { id?, name, balance? } | null
 */
const CreditTabPicker = ({ value, onChange, currency }) => {
  const { t } = useI18n();
  const [query, setQuery] = useState(value?.name || "");
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  const requestRef = useRef(0);

  useEffect(() => {
    const term = query.trim();
    if (!open) return undefined;

    const request = ++requestRef.current;
    const timer = setTimeout(async () => {
      try {
        const { data } = await posAPI.searchTabs(term);
        // A slower answer for an older search must not replace a newer one
        if (request === requestRef.current) setMatches(data);
      } catch {
        if (request === requestRef.current) setMatches([]);
      }
    }, SEARCH_DELAY_MS);

    return () => clearTimeout(timer);
  }, [query, open]);

  if (value?.name && !open) {
    return (
      <div className="flex items-center gap-3">
        <span className="shrink-0 text-lg" aria-hidden="true">
          🤝
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white truncate">{value.name}</p>
          <p className="text-xs text-amber-200/80 truncate">
            {value.id
              ? Number(value.balance) > 0
                ? t("pos.tab.owes", { amount: formatMoney(value.balance, currency) })
                : t("pos.tab.nothingOwed")
              : t("pos.tab.newTab")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setQuery(value.name);
            setOpen(true);
          }}
          className="shrink-0 h-9 px-3 rounded-lg bg-slate-800 border border-slate-700 text-xs font-bold text-slate-300 hover:bg-slate-700"
        >
          {t("pos.tab.change")}
        </button>
      </div>
    );
  }

  const term = query.trim();
  const exact = matches.find((tab) => sameName(tab.name, term));

  const choose = (tab) => {
    onChange(tab);
    setOpen(false);
  };

  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-semibold uppercase tracking-wide text-amber-200/80">
        {t("pos.tab.label")}
      </label>
      <input
        type="text"
        value={query}
        autoComplete="off"
        placeholder={t("pos.tab.placeholder")}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          // What was typed is the tab until something is picked from the list
          onChange(event.target.value.trim() ? { name: event.target.value } : null);
        }}
        className="w-full h-11 px-3 rounded-xl bg-slate-900 border border-slate-600 text-white placeholder-slate-500 focus:outline-none focus:border-amber-400"
      />

      {open && (matches.length > 0 || term) && (
        <ul className="rounded-xl border border-slate-700 bg-slate-900 divide-y divide-slate-800 max-h-56 overflow-y-auto">
          {matches.map((tab) => (
            <li key={tab.id}>
              <button
                type="button"
                onClick={() => choose(tab)}
                className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-start hover:bg-slate-800"
              >
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-white truncate">
                    {tab.name}
                  </span>
                  {tab.phone && (
                    <span className="block text-xs text-slate-400" dir="ltr">
                      ••• {String(tab.phone).slice(-4)}
                    </span>
                  )}
                </span>
                {Number(tab.balance) > 0 && (
                  <span className="shrink-0 text-xs font-semibold text-amber-400 tabular-nums">
                    {formatMoney(tab.balance, currency)}
                  </span>
                )}
              </button>
            </li>
          ))}
          {term && !exact && (
            <li>
              <button
                type="button"
                onClick={() => choose({ name: term })}
                className="w-full px-3 py-2.5 text-start text-sm font-semibold text-emerald-300 hover:bg-slate-800"
              >
                ＋ {t("pos.tab.open", { name: term })}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default CreditTabPicker;
